import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceType, ServiceCategory } from '../../../../models/ServiceType';
import { ServiceTypeService } from '../../../../services/service-type.service';
import { StatusService } from '../../../../services/status.service';
import { Status } from '../../../../models/Status';

@Component({
  selector: 'app-services-form',
  templateUrl: './services-form.component.html',
  styleUrls: ['./services-form.component.css']
})
export class ServicesFormComponent implements OnInit {
  form!: FormGroup;
  isEditMode = false;
  loading = false;
  error: string | null = null;
  submitted = false;
  serviceId?: number;
  
  statuses: Status[] = [];
  
  categories = Object.values(ServiceCategory).map(value => ({
    value: value,
    label: value
  }));

  constructor(
    private fb: FormBuilder,
    private serviceTypeService: ServiceTypeService,
    private statusService: StatusService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadStatuses();
    
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.serviceId = +params['id'];
        this.loadService(this.serviceId);
      }
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', [Validators.maxLength(1000)]],
      code: ['', [Validators.maxLength(50)]],
      category: [''],
      estimatedTimeMinutes: [0, [Validators.min(0)]],
      baseCost: [0, [Validators.min(0)]],
      currentCost: [0, [Validators.min(0)]],
      profitMarginPercentage: [0, [Validators.min(0), Validators.max(999.99)]],
      status: ['', [Validators.required]],
      isActive: [true]
    });
  }

  loadStatuses(): void {
    this.statusService.getByEntity('ServiceType').subscribe({
      next: (statuses: Status[]) => {
        this.statuses = statuses;
      },
      error: (err: any) => {
        console.error('Erro ao carregar status:', err);
      }
    });
  }

  loadService(id: number): void {
    this.loading = true;
    this.serviceTypeService.getById(id).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const service = response.data;
          this.form.patchValue({
            name: service.name,
            description: service.description,
            code: service.code,
            category: service.category,
            estimatedTimeMinutes: service.estimatedTimeMinutes,
            baseCost: service.baseCost,
            currentCost: service.currentCost,
            profitMarginPercentage: service.profitMarginPercentage,
            status: service.status,
            isActive: service.isActive
          });
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Erro ao carregar serviço.';
        this.loading = false;
        console.error(err);
      }
    });
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.form.invalid) {
      return;
    }

    this.loading = true;
    this.error = null;

    const formValue = this.form.value;
    
    // Find selected status to get isActive flag if needed, though we have isActive in form
    const selectedStatus = this.statuses.find((s: Status) => s.name === formValue.status);
    
    // Construct service object
    const serviceData: Partial<ServiceType> = {
      ...formValue,
      isActive: selectedStatus ? selectedStatus.isActive : formValue.isActive
    };

    const request = this.isEditMode && this.serviceId
      ? this.serviceTypeService.update(this.serviceId, serviceData)
      : this.serviceTypeService.create(serviceData);

    request.subscribe({
      next: (response) => {
        if (response.success) {
          this.router.navigate(['/operations/services']);
        } else {
          this.error = response.message || 'Erro ao salvar serviço.';
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Erro ao salvar serviço. Tente novamente.';
        this.loading = false;
        console.error(err);
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/operations/services']);
  }

  // Getters for form controls
  get name() { return this.form.get('name'); }
  get description() { return this.form.get('description'); }
  get code() { return this.form.get('code'); }
  get estimatedTimeMinutes() { return this.form.get('estimatedTimeMinutes'); }
  get baseCost() { return this.form.get('baseCost'); }
  get currentCost() { return this.form.get('currentCost'); }
  get profitMarginPercentage() { return this.form.get('profitMarginPercentage'); }

  // Helpers
  formatTimePreview(): string {
    const minutes = this.estimatedTimeMinutes?.value || 0;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? mins + 'min' : ''}`;
    }
    return `${mins}min`;
  }

  calculatePreview(): number {
    const cost = this.currentCost?.value || this.baseCost?.value || 0;
    const margin = this.profitMarginPercentage?.value || 0;
    
    return cost + (cost * (margin / 100));
  }
}
